import React, { useCallback, useState } from 'react';
import { Box, IconButton, TextField } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { IsoDatePicker } from './IsoDatePicker';
import { FixedField } from './FixedField';
import { isoDateToMMM } from '../utils';

// ---------------------------------------------------------------------------
// Datetime helpers
//
// The wire format is the `Y-m-d H:i:s` string the backend DateTime scalar
// expects. The date/time split exists only here and inside the control: it is
// never stored in form state.
// ---------------------------------------------------------------------------

// Composes a date (YYYY-MM-DD) and time (HH:mm) into the Y-m-d H:i:s wire
// string. Seconds are fixed at :00. A populated date with a blank/missing
// time maps to 00:00 (a date alone is a valid datetime at midnight). Only a
// missing date returns null.
export const composeIsoDateTime = (date?: string | null, time?: string | null): string | null => {
    if (!date) return null;
    const t = (time && time.trim()) ? time.trim() : '00:00';
    return `${date} ${t}:00`;
};

// Splits a Y-m-d H:i:s wire string (or empty) back into date + time parts
// for display. Returns nulls when the value is unset. Seconds are dropped —
// the control never presents them.
export const splitIsoDateTime = (wire?: string | null): { date: string | null, time: string | null } => {
    if (!wire) return { date: null, time: null };
    const [date, timePart] = wire.split(' ');
    // wire is "YYYY-MM-DD HH:mm:ss" — we only want HH:mm
    const time = timePart ? timePart.substring(0, 5) : null;
    return { date: date || null, time };
};

// Restricts the time input as the user types: digits only, plus at most one
// colon, capped at 5 characters (HH:mm). Anything else is never admitted.
export const sanitizeTimeInput = (raw?: string | null): string => {
    if (!raw) return '';
    let out = '';
    let seenColon = false;
    for (const ch of String(raw)) {
        if (ch >= '0' && ch <= '9') {
            out += ch;
        } else if (ch === ':' && !seenColon) {
            seenColon = true;
            out += ch;
        }
        if (out.length >= 5) break;
    }
    return out.slice(0, 5);
};

// A committed time must be a full HH:mm: hour 00-23, minute 00-59.
export const isValidTimeInput = (time?: string | null): boolean =>
    !!time && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time);

const TIME_FORMAT_ERROR = 'Enter a time as HH:mm (00:00 – 23:59)';

export interface ISODateTimeFieldProps {
    // The single bound value: 'YYYY-MM-DD HH:mm:ss' or null. No separate date
    // or time value ever reaches the caller.
    name: string;
    value?: string | null;
    onChange: (value: string | null) => void;
    label?: string;
    // Caller-supplied error: a flag, or the message to show.
    error?: boolean | string;
    readOnly?: boolean;
    disabled?: boolean;
    className?: string;
    dateLabel?: string;
    timeLabel?: string;
}

// A date-and-time form control bound to ONE datetime value: an IsoDatePicker
// plus an HH:mm text field. Invalid in-progress text is held inside the
// control and never reaches the bound value; the time input is disabled until
// a date is selected.
//
//   keystroke --> sanitize (digits + one colon, max 5)
//       +-- valid HH:mm  --> commit compose(date, time) immediately
//       +-- empty        --> commit compose(date, null) (midnight), keep the
//                          field visually empty while focused
//       +-- partial      --> hold in draft, bound value unchanged
//       +-- blur invalid --> revert displayed time to the committed value
//                           + inline error; bound value unchanged
export const ISODateTimeField = (props: ISODateTimeFieldProps): React.ReactElement => {
    const { name, value, onChange, label, error, readOnly, disabled, className } = props;
    const dateLabel = props.dateLabel ?? label;
    const timeLabel = props.timeLabel ?? 'Time';

    const { date, time: committedTime } = splitIsoDateTime(value);
    // The in-progress time text. null means "no draft — show the committed
    // time", so a re-render never clobbers what the user is typing and a
    // committed value always follows the bound value.
    const [draftTime, setDraftTime] = useState<string | null>(null);
    const [timeError, setTimeError] = useState<string | null>(null);

    const displayTime = draftTime !== null ? draftTime : (committedTime ?? '');

    const callerErrorText = typeof error === 'string' && error ? error : undefined;
    const callerHasError = !!error;

    const handleDateChange = useCallback((newDate: string | null) => {
        setTimeError(null);
        if (!newDate) {
            // Clearing the date clears the whole value, the time draft, and
            // (because there is no date) re-disables the time input.
            setDraftTime(null);
            onChange(null);
            return;
        }
        // Compose with the currently committed time — a partial draft is not
        // committed, so it must not ride along into the new value.
        onChange(composeIsoDateTime(newDate, committedTime));
    }, [committedTime, onChange]);

    const handleTimeChange = useCallback((raw: string) => {
        const t = sanitizeTimeInput(raw);
        if (t === '') {
            // Cleared time with a date kept => midnight. Keep the draft empty
            // so the field does not fill itself in mid-edit.
            setDraftTime('');
            setTimeError(null);
            onChange(composeIsoDateTime(date, null));
            return;
        }
        if (isValidTimeInput(t)) {
            // Drop the draft: the committed value now carries the same text.
            setDraftTime(null);
            setTimeError(null);
            onChange(composeIsoDateTime(date, t));
            return;
        }
        // Partial (e.g. "09:3") — hold it locally, bound value unchanged.
        setDraftTime(t);
    }, [date, onChange]);

    const handleTimeBlur = useCallback(() => {
        if (draftTime === null) return;
        if (draftTime === '' || isValidTimeInput(draftTime)) {
            setDraftTime(null);
            setTimeError(null);
            return;
        }
        // Not a valid time: revert the display to the last committed value
        // and say why. The bound value was never touched.
        setDraftTime(null);
        setTimeError(TIME_FORMAT_ERROR);
    }, [draftTime]);

    if (readOnly) {
        const text = date ? `${isoDateToMMM(date)}${committedTime ? ' ' + committedTime : ''}` : '';
        return (
            <FixedField
                label={label}
                value={text}
                className={className}
                InputProps={{ readOnly: true }}
            />
        );
    }

    return (
        <Box className={className} display="flex" alignItems="flex-start" gap={1}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <IsoDatePicker
                    name={name}
                    label={dateLabel}
                    value={date ?? ''}
                    onChange={(evt: any) => handleDateChange(evt?.target?.value ?? null)}
                    error={callerHasError}
                    readOnly={readOnly}
                    readOnlyMode="field"
                    fill={true}
                />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                <TextField
                    label={timeLabel}
                    name={`${name}_time`}
                    size="small"
                    value={displayTime}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    onBlur={handleTimeBlur}
                    disabled={!date || disabled}
                    error={callerHasError || !!timeError}
                    helperText={timeError ?? callerErrorText}
                    inputProps={{ placeholder: 'HH:mm', maxLength: 5 }}
                    fullWidth
                />
            </Box>
            {/* IsoDatePicker exposes no clear affordance of its own, so the
                control provides one: clearing the date clears the whole value
                (and with it the time, which is then disabled again). */}
            {!!value && !disabled && (
                <IconButton
                    size="small"
                    aria-label={`Clear ${label ?? name}`}
                    onClick={() => handleDateChange(null)}
                    sx={{ mt: 0.5 }}
                >
                    <ClearIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
};

export default ISODateTimeField;
